import {
    collection,
    // doc, // unused
    getDocs,
    // getDoc, // unused
    query,
    where,
    orderBy,
    limit
} from "firebase/firestore";
import { db, functions } from "./firebase";
import { httpsCallable } from "firebase/functions";

/**
 * Helper function to safely convert date formats
 */
const convertToDate = (dateValue) => {
    if (!dateValue) return null;
    if (dateValue instanceof Date) return dateValue;
    if (dateValue.toDate) return dateValue.toDate();
    if (dateValue.seconds) return new Date(dateValue.seconds * 1000);
    return new Date(dateValue);
};

// ============================================================================
// CACHING UTILITY WITH ROBUST DEDUPLICATION
// ============================================================================

// Global variable to hold the pending network request
// We use a Map to handle potential multi-store scenarios, though usually it's just one.
const activeRequests = new Map();

const getCachedActiveSales = async (storeId) => {
    const cacheKey = `activeSales_${storeId}`;

    // 1. Check In-Memory Promise (Deduplication)
    // If a request is already running for this store, return that existing promise
    if (activeRequests.has(cacheKey)) {
        // console.log("🔄 reusing in-flight sales promise");
        return activeRequests.get(cacheKey);
    }

    // 2. Check Session Storage (Fast Cache)
    const cached = sessionStorage.getItem(cacheKey);
    const cachedTs = sessionStorage.getItem(`${cacheKey}_ts`);

    if (cached && cachedTs && (Date.now() - parseInt(cachedTs) < 5 * 60 * 1000)) {
        // console.log("💾 using cached sales data");
        return JSON.parse(cached);
    }

    // 3. Start New Network Request
    const fetchPromise = (async () => {
        try {
            // console.log("🌐 fetching sales from network...");
            const salesRef = collection(db, "stores", storeId, "sales");
            const q = query(
                salesRef,
                where("isActive", "==", true),
                orderBy("endDate", "desc")
            );

            const querySnapshot = await getDocs(q);
            const currentDate = new Date();
            const activeSales = [];

            querySnapshot.forEach((doc) => {
                const saleData = doc.data();
                const startDate = convertToDate(saleData.startDate);
                const endDate = convertToDate(saleData.endDate);

                const isStarted = !startDate || startDate <= currentDate;
                const isNotExpired = !endDate || endDate > currentDate;

                if (isStarted && isNotExpired) {
                    activeSales.push({
                        id: doc.id,
                        ...saleData,
                        startDate: startDate ? startDate.toISOString() : null,
                        endDate: endDate ? endDate.toISOString() : null
                    });
                }
            });

            // Update Session Storage
            sessionStorage.setItem(cacheKey, JSON.stringify(activeSales));
            sessionStorage.setItem(`${cacheKey}_ts`, Date.now().toString());

            return activeSales;
        } catch (error) {
            console.error("Error fetching sales:", error);
            throw error;
        } finally {
            // Remove from active requests ONLY after it settles
            activeRequests.delete(cacheKey);
        }
    })();

    // Store the promise in the map
    activeRequests.set(cacheKey, fetchPromise);

    return fetchPromise;
};

// ============================================================================
// EXPORTED FUNCTIONS
// ============================================================================

// Helper to invalidate cache on writes
const invalidateSalesCache = (storeId) => {
    sessionStorage.removeItem(`activeSales_${storeId}`);
    activeRequests.delete(`activeSales_${storeId}`);
};

export const addToSales = async (storeId, saleData) => {
    try {
        if (!storeId) throw new Error("Store ID is required");
        const fn = httpsCallable(functions, 'addToSales');
        const result = await fn({ storeId, saleData });
        if (result.data.success) {
            invalidateSalesCache(storeId);
            return result.data.saleId;
        }
        throw new Error(result.data.message);
    } catch (error) {
        console.error('Error adding to sales:', error);
        throw error;
    }
};

export const removeFromSales = async (storeId, saleId) => {
    try {
        const fn = httpsCallable(functions, 'removeFromSales');
        const result = await fn({ storeId, saleId });
        if (result.data.success) {
            invalidateSalesCache(storeId);
            return result.data;
        }
        throw new Error(result.data.message);
    } catch (error) {
        console.error('Error removing from sales:', error);
        throw error;
    }
};

export const updateSale = async (storeId, saleId, updateData) => {
    try {
        const fn = httpsCallable(functions, 'updateSale');
        const result = await fn({ storeId, saleId, saleData: updateData });
        if (result.data.success) {
            invalidateSalesCache(storeId);
            return result.data;
        }
        throw new Error(result.data.message);
    } catch (error) {
        console.error('Error updating sale:', error);
        throw error;
    }
};

export const toggleSaleStatus = async (storeId, saleId) => {
    try {
        const fn = httpsCallable(functions, 'toggleSaleStatus');
        const result = await fn({ storeId, saleId });
        if (result.data.success) {
            invalidateSalesCache(storeId);
            return result.data;
        }
        throw new Error(result.data.message);
    } catch (error) {
        console.error('Error toggling sale status:', error);
        throw error;
    }
};

export const getSalesItems = async (storeId, options = {}) => {
    try {
        const salesRef = collection(db, "stores", storeId, "sales");
        let q = salesRef;
        if (options.activeOnly) q = query(q, where("isActive", "==", true));
        if (options.productId) q = query(q, where("productId", "==", options.productId));
        q = query(q, orderBy("createdAt", "desc"));
        if (options.limit) q = query(q, limit(options.limit));

        const querySnapshot = await getDocs(q);
        const salesItems = [];
        querySnapshot.forEach((doc) => {
            salesItems.push({ id: doc.id, ...doc.data() });
        });
        return salesItems;
    } catch (error) {
        console.error("Error getting sales items:", error);
        throw error;
    }
};

/**
 * Optimized: Uses the shared promise
 */
export const getActiveSales = async (storeId) => {
    return await getCachedActiveSales(storeId);
};

/**
 * Optimized: Uses the shared promise
 */
export const getSalesByProductId = async (storeId, productId) => {
    try {
        const activeSales = await getCachedActiveSales(storeId);
        return activeSales.filter(sale => sale.productId === productId);
    } catch (error) {
        console.error("Error getting sales by product ID:", error);
        return [];
    }
};

/**
 * Optimized: Uses the shared promise
 */
export const getSaleProductIds = async (storeId) => {
    try {
        const activeSales = await getCachedActiveSales(storeId);
        return [...new Set(activeSales.map(sale => sale.productId))].filter(Boolean);
    } catch (error) {
        console.error("Error getting sale product IDs:", error);
        throw error;
    }
};

export const calculateDiscountedPrice = (originalPrice, discount) => {
    if (!discount) return originalPrice;
    const price = parseFloat(originalPrice) || 0;
    const discountType = discount.discountType || discount.type;
    const discountValue = parseFloat(discount.discountValue || discount.value) || 0;
    if (discountType === "percentage") {
        return price * (1 - discountValue / 100);
    }
    return Math.max(0, price - discountValue);
};

export default {
    addToSales, removeFromSales, updateSale, getSalesItems, getActiveSales,
    getSalesByProductId, calculateDiscountedPrice, getSaleProductIds,
    toggleSaleStatus
};